import * as React from 'react';
import createPersistedState from 'use-persisted-state';
import { storageFactory } from './StorageFactory';

const STORAGE_KEY = 'APP_VERSION';

const localStore = storageFactory(() => localStorage);

const defaultProps = {
  duration: 60 * 1000,
  auto: false,
  storageKey: STORAGE_KEY,
  basePath: '',
  localStore: localStore
};

type OwnProps = {
  children?: any;
} & typeof defaultProps;

export const useClearCache = (props?: OwnProps) => {
  let { duration, auto, storageKey, basePath, localStore } = {
    ...defaultProps,
    ...props
  };

  const [loading, setLoading] = React.useState(true);
  const useAppVersionState = createPersistedState(storageKey, localStore);
  const [appVersion, setAppVersion] = useAppVersionState('');
  const [isLatestVersion, setIsLatestVersion] = React.useState(true);
  const [latestVersion, setLatestVersion] = React.useState(appVersion);

  async function setVersion(version: string) {
    await setAppVersion(version);
  }

  const emptyCacheStorage = async (version?: string) => {
    if ('caches' in window) {
      // Service worker cache should be cleared with caches.delete()
      caches.keys().then(names => {
        // eslint-disable-next-line no-restricted-syntax
        for (const name of names) caches.delete(name);
      });
    }

    // clear browser cache and reload page
    await setVersion(version || latestVersion).then(() =>
      window.location.reload(true)
    );
  };

  // Replace any last slash with an empty space
  const baseUrl = basePath.replace(/\/+$/, '') + '/meta.json';

  function fetchMeta() {
    try {
      fetch(baseUrl, {
        cache: 'no-store'
      })
        .then(response => response.json())
        .then(meta => {
          const newVersion = meta.version;
          const currentVersion = appVersion;
          const isUpdated = newVersion === currentVersion || appVersion === '';
          if (!isUpdated && !auto) {
            setLatestVersion(newVersion);
            setLoading(false);
            if (appVersion) {
              setIsLatestVersion(false);
            } else {
              setVersion(newVersion);
            }
          } else if (!isUpdated && auto) {
            console.log("Update found2!");
            setTimeout(() => {
              emptyCacheStorage(newVersion);
            },60000);
          } else {
            if (appVersion === '') {
              setVersion(newVersion);
            }
            setIsLatestVersion(true);
            setLoading(false);
          }
        }).catch(() => {
        setLoading(false);
      });
    } catch (err) {
      console.error(err);
    }
  }

  React.useEffect(() => {
    const fetchCacheTimeout = setInterval(() => fetchMeta(), duration);
    return () => {
      clearInterval(fetchCacheTimeout);
    };
  }, [loading]);

  React.useEffect(() => {
    fetchMeta();
  }, []);

  return {
    loading,
    isLatestVersion,
    emptyCacheStorage,
    latestVersion
  };
};

const ClearCache: React.FC<OwnProps> = props => {
  const { loading, isLatestVersion, emptyCacheStorage } = useClearCache(props);

  const { children } = props;

  return children({
    loading,
    isLatestVersion,
    emptyCacheStorage
  });
};

export default ClearCache;
